import { useEffect, useRef, useState } from "react";
import { FileUp, Loader2, X } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  ClientMaterial,
  DuplicateExisting,
  MATERIAL_ACCEPT,
  MATERIAL_ALLOWED_EXTENSIONS,
  MATERIAL_CATEGORY_LABEL,
  MATERIAL_MAX_BYTES,
  MaterialCategory,
  MaterialCreateResult,
  MaterialUploadAborted,
  MaterialUploadError,
  fetchMaterialRequestStatus,
  formatFileSize,
  newClientRequestId,
  uploadClientMaterial,
} from "@/api/clientMaterials";

/**
 * D-146 W2 §8/§9 — «Додати матеріал» modal (mockup v0.4 LOCK; mobile fullscreen).
 *
 * - holding binding is READ-ONLY (model-level, v1);
 * - name* + category* required, submit disabled until valid file + fields;
 * - honest transport progress (XHR bytes) + cancel; after an abort the FE
 *   reconciles by clientRequestId: absent/failed → «скасовано», committed →
 *   the server won the race — close + refresh, pending → bounded poll;
 * - duplicate 409 → explicit choice («Відкрити наявний» / «Завантажити
 *   окремо» = confirmDuplicate retry under the SAME idempotency key);
 * - every server reject is a structured message, never a dead end.
 */

type Phase =
  | { kind: "form" }
  | { kind: "uploading"; percent: number }
  | { kind: "reconciling" }
  | { kind: "error"; message: string; retryable: boolean }
  | { kind: "duplicate"; existing: DuplicateExisting };

const CATEGORY_KEYS: MaterialCategory[] = ["kp", "matrix", "marketing", "other"];

export function MaterialUploadModal({
  holdingId,
  holdingName,
  onClose,
  onSuccess,
  onOpenExisting,
}: {
  holdingId: string;
  holdingName: string;
  onClose: () => void;
  onSuccess: (item: ClientMaterial, opts?: { serverWonRace?: boolean }) => void;
  onOpenExisting?: (existingId: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [category, setCategory] = useState<MaterialCategory | "">("");
  const [phase, setPhase] = useState<Phase>({ kind: "form" });
  const [requestId, setRequestId] = useState(newClientRequestId);
  const abortRef = useRef<(() => void) | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const busy = phase.kind === "uploading" || phase.kind === "reconciling";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const pickFile = (f: File | null) => {
    setFileError(null);
    setPhase({ kind: "form" });
    if (!f) {
      setFile(null);
      return;
    }
    const ext = (f.name.split(".").pop() ?? "").toLowerCase();
    if (["docm", "xlsm", "pptm"].includes(ext)) {
      setFile(null);
      setFileError("Формати з макросами (.docm/.xlsm/.pptm) не підтримуються.");
      return;
    }
    if (!MATERIAL_ALLOWED_EXTENSIONS.includes(ext)) {
      setFile(null);
      setFileError(`Формат .${ext || "?"} не підтримується. Дозволено: PDF, DOCX, XLSX, PPTX, JPG, PNG.`);
      return;
    }
    if (f.size > MATERIAL_MAX_BYTES) {
      setFile(null);
      setFileError(`Файл завеликий (${Math.round(f.size / 1048576)} МБ). Максимум — 20 МБ.`);
      return;
    }
    setFile(f);
    // Новий бінарник = нова спроба = новий idempotency-ключ (TZ §9).
    setRequestId(newClientRequestId());
    if (!nameTouched || name.trim() === "") {
      setName(f.name.replace(/\.[^.]+$/, "").replace(/[_]+/g, " ").trim());
    }
  };

  const valid = !!file && name.trim().length > 0 && name.trim().length <= 255 && category !== "";

  const startUpload = (confirmDuplicate: boolean) => {
    if (!file || category === "") return;
    setPhase({ kind: "uploading", percent: 0 });
    const handle = uploadClientMaterial(
      holdingId,
      file,
      { name: name.trim(), category, clientRequestId: requestId, confirmDuplicate },
      (loaded, total) =>
        setPhase({ kind: "uploading", percent: total > 0 ? Math.round((loaded / total) * 100) : 0 }),
    );
    abortRef.current = handle.abort;
    handle.promise
      .then((res: MaterialCreateResult) => onSuccess(res.item))
      .catch((e: unknown) => {
        if (e instanceof MaterialUploadAborted) {
          void reconcileAfterAbort();
          return;
        }
        if (e instanceof MaterialUploadError) {
          if (e.code === "duplicate_material" && e.existing) {
            setPhase({ kind: "duplicate", existing: e.existing });
            return;
          }
          if (e.code === "upload_pending") {
            void reconcileAfterAbort();
            return;
          }
          setPhase({ kind: "error", message: e.message, retryable: true });
          return;
        }
        setPhase({ kind: "error", message: "Не вдалося завантажити файл. Спробуйте ще раз.", retryable: true });
      })
      .finally(() => {
        abortRef.current = null;
      });
  };

  /** TZ §9: після abort — джерело істини server (absent|failed|committed|pending). */
  const reconcileAfterAbort = async () => {
    setPhase({ kind: "reconciling" });
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const st = await fetchMaterialRequestStatus(holdingId, requestId);
        if (st.status === "committed") {
          onSuccess(st.item ?? ({ id: st.documentId } as ClientMaterial), { serverWonRace: true });
          return;
        }
        if (st.status === "absent" || st.status === "failed") {
          setPhase({ kind: "error", message: "Завантаження скасовано.", retryable: true });
          return;
        }
        // pending → bounded poll
      } catch {
        // transient — keep polling within the bound
      }
      await new Promise((r) => window.setTimeout(r, 1000));
    }
    setPhase({
      kind: "error",
      message: "Стан завантаження невідомий. Оновіть список — якщо матеріалу немає, спробуйте ще раз.",
      retryable: true,
    });
  };

  const cancelUpload = () => abortRef.current?.();

  const duplicate = phase.kind === "duplicate" ? phase.existing : null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center sm:p-6"
      onClick={() => !busy && onClose()}
      aria-hidden
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Додати матеріал"
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[92vh] w-full flex-col overflow-y-auto rounded-t-2xl bg-background p-5 shadow-2xl sm:max-w-md sm:rounded-2xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">Додати матеріал</h2>
          <button
            type="button"
            aria-label="Закрити"
            disabled={busy}
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* File zone / card */}
        {!file ? (
          <label
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              pickFile(e.dataTransfer.files?.[0] ?? null);
            }}
            className="mb-4 flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed bg-muted/40 px-4 py-7 text-center text-sm text-muted-foreground hover:border-primary/50"
          >
            <FileUp className="h-5 w-5" aria-hidden />
            <span>
              Перетягніть файл або <span className="font-semibold text-primary">оберіть</span>
            </span>
            <span className="text-xs">PDF, DOCX, XLSX, PPTX, JPG, PNG · до 20 МБ</span>
            <input
              ref={fileInputRef}
              type="file"
              accept={MATERIAL_ACCEPT}
              className="sr-only"
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
            />
          </label>
        ) : (
          <div className="mb-4 flex items-center gap-3 rounded-lg border px-3 py-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-600 text-[10px] font-bold uppercase text-white">
              {(file.name.split(".").pop() ?? "").slice(0, 4)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{file.name}</p>
              <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
            </div>
            {!busy && (
              <>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  Замінити
                </button>
                <button
                  type="button"
                  onClick={() => pickFile(null)}
                  className="text-xs font-semibold text-muted-foreground hover:underline"
                >
                  Прибрати
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={MATERIAL_ACCEPT}
                  className="sr-only"
                  onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
                />
              </>
            )}
          </div>
        )}

        {fileError && (
          <div role="alert" className="mb-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {fileError}
          </div>
        )}

        {/* Holding — read-only binding (v1). */}
        <div className="mb-3">
          <span className="mb-1 block text-xs font-semibold">Зберігається для</span>
          <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm">Холдинг «{holdingName}»</div>
          <p className="mt-1 text-[11px] text-muted-foreground">Матеріал буде доступний у картці всього холдингу.</p>
        </div>

        <div className="mb-3">
          <label className="mb-1 block text-xs font-semibold" htmlFor="material-name">
            Назва матеріалу <span className="text-destructive">*</span>
          </label>
          <input
            id="material-name"
            value={name}
            maxLength={255}
            disabled={busy}
            onChange={(e) => {
              setNameTouched(true);
              setName(e.target.value);
            }}
            className="w-full rounded-lg border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">Оригінальна назва файлу зберігається окремо.</p>
        </div>

        <div className="mb-4">
          <label className="mb-1 block text-xs font-semibold" htmlFor="material-category">
            Категорія <span className="text-destructive">*</span>
          </label>
          <select
            id="material-category"
            value={category}
            disabled={busy}
            onChange={(e) => setCategory(e.target.value as MaterialCategory | "")}
            className="w-full rounded-lg border bg-card px-3 py-2 text-sm"
          >
            <option value="">— Оберіть категорію —</option>
            {CATEGORY_KEYS.map((c) => (
              <option key={c} value={c}>{MATERIAL_CATEGORY_LABEL[c]}</option>
            ))}
          </select>
        </div>

        {/* Progress / reconcile / errors / duplicate */}
        {phase.kind === "uploading" && (
          <div className="mb-4">
            <div
              role="progressbar"
              aria-label="Завантаження матеріалу"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={phase.percent}
              className="h-1.5 overflow-hidden rounded-full bg-muted"
            >
              <div className="h-full bg-primary transition-[width]" style={{ width: `${phase.percent}%` }} />
            </div>
            <p aria-live="polite" className="mt-1.5 text-xs text-muted-foreground">
              {phase.percent}% · {file?.name}
            </p>
          </div>
        )}

        {phase.kind === "reconciling" && (
          <div aria-live="polite" className="mb-4 flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Перевіряємо, чи файл встиг зберегтися…
          </div>
        )}

        {phase.kind === "error" && (
          <div role="alert" className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
            {phase.message}
          </div>
        )}

        {duplicate && (
          <div className="mb-4 rounded-lg border border-warning-foreground/25 bg-warning px-3 py-2.5 text-sm text-warning-foreground">
            <p className="font-medium">Матеріал зі схожою назвою вже є в цьому холдингу:</p>
            <div className="my-2 rounded-md border border-warning-foreground/20 bg-background px-2.5 py-2 text-foreground">
              <p className="text-sm font-semibold">{duplicate.name}</p>
              <p className="text-xs text-muted-foreground">
                додано {duplicate.createdAt.slice(0, 10)}{duplicate.createdBy ? ` · ${duplicate.createdBy}` : ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {onOpenExisting && (
                <button
                  type="button"
                  onClick={() => onOpenExisting(duplicate.id)}
                  className="rounded-md border border-warning-foreground/30 px-2.5 py-1.5 text-xs font-semibold"
                >
                  Відкрити наявний
                </button>
              )}
              <button
                type="button"
                onClick={() => startUpload(true)}
                className="rounded-md bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground"
              >
                Завантажити окремо
              </button>
            </div>
            <p className="mt-1.5 text-[11px]">
              Заміна файлу «поверх» неможлива — новий бінарник = завжди новий запис (аудит).
            </p>
          </div>
        )}

        <div className="mt-auto flex justify-end gap-2">
          {phase.kind === "uploading" ? (
            <button
              type="button"
              onClick={cancelUpload}
              className="rounded-lg border px-3.5 py-2 text-sm font-medium hover:bg-accent"
            >
              Скасувати
            </button>
          ) : (
            <button
              type="button"
              disabled={phase.kind === "reconciling"}
              onClick={onClose}
              className="rounded-lg border px-3.5 py-2 text-sm font-medium hover:bg-accent disabled:opacity-40"
            >
              Скасувати
            </button>
          )}
          <button
            type="button"
            disabled={!valid || busy || phase.kind === "duplicate"}
            onClick={() => startUpload(false)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground",
              (!valid || busy || phase.kind === "duplicate") && "cursor-not-allowed opacity-50",
            )}
          >
            {phase.kind === "uploading" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Завантажити
          </button>
        </div>
      </div>
    </div>
  );
}
